import * as Sentry from '@sentry/react'

import { Product } from 'even-ts-static'

export type ErrorType = string | Error | ErrorEvent

export const captureTrackingImpressionError = (
  asset: string,
  companyUuid: string,
  leadUuid: string,
  referrer: string,
  error: ErrorType,
) => {
  Sentry.captureMessage('Tracking Impression Failed', {
    extra: {
      asset,
      companyUuid,
      error,
      leadUuid,
      referrer,
    },
  })
}

export const captureGetCombinedConfigError = (error: ErrorType) => {
  Sentry.captureMessage('getCombinedConfig failure', { extra: { error } })
}

export const captureGetPartnerConfigError = (error: ErrorType) => {
  Sentry.captureMessage('getPartnerConfig failure', { extra: { error } })
}

export const captureTrackingAppnexusIdWarning = (error: ErrorType) => {
  Sentry.captureMessage('Appnexus ID Capture Failed', {
    extra: {
      error,
    },
    level: Sentry.Severity.Warning,
  })
}

export const captureGoogleMapsError = (error: ErrorType) => {
  Sentry.captureMessage('Google Maps Client Request Failed', {
    extra: {
      error,
    },
  })
}

export const captureCookieCapabilityError = () => {
  Sentry.captureMessage('Cookie Storage Failed', {
    level: Sentry.Severity.Warning,
  })
}

export const captureTrackingEventError = (action: string, trackingUuid: string, error: ErrorType) => {
  Sentry.captureMessage('Tracking Event Failed', {
    extra: {
      action,
      error,
      trackingUuid,
    },
  })
}

export const captureTrackingWithNoUuidError = (action: string, error: ErrorType) => {
  Sentry.captureMessage('Tracking Event with No UUID', {
    extra: {
      action,
      error,
    },
  })
}

export const capturePartnerConfigError = (partnerApiUrl: string, error: ErrorType) => {
  Sentry.captureMessage('Get Partner Config Failed', {
    extra: {
      error,
      partnerApiUrl,
    },
  })
}

export const captureParentDataError = (error: ErrorType) => {
  Sentry.captureMessage('Get Parent Data Failed', {
    extra: {
      error,
    },
  })
}

export const captureFetchPreviewOffersError = (product: Product, error: ErrorType) => {
  Sentry.captureMessage('Error fetching loan preview offers', {
    extra: {
      error,
      product,
    },
  })
}

export const captureGeocoderError = (params: object, error: ErrorType) => {
  Sentry.captureMessage('Geocoder Failed', {
    extra: {
      error,
      params,
    },
  })
}

export const captureRefreshRateTablesError = (product: Product, rateTableUuid: string, error: ErrorType) => {
  Sentry.captureMessage('Rate Table Refresh Failed', {
    extra: {
      error,
      product,
      rateTableUuid,
    },
  })
}

export const captureFacebookPixelTrackingEventError = (action: string, error: ErrorType) => {
  // Temporarily silenced
  // Sentry.captureMessage('Facebook Pixel Tracking Event Failed', {
  //   extra: {
  //     action,
  //     error,
  //   },
  // })
}

export const captureGetStaticOfferError = (product: Product, offerUuid: string, error: ErrorType) => {
  Sentry.captureMessage('Get Static Offer Failed', {
    extra: {
      error,
      offerUuid,
      product,
    },
  })
}

export const captureGetRateTablesError = (product: Product, error: ErrorType) => {
  Sentry.captureMessage('Get Rate Tables Failed', {
    extra: {
      error,
      product,
    },
  })
}

export const captureGeolocateError = (product: Product, error: ErrorType) => {
  Sentry.captureMessage('Geolocate from IP Failed', {
    extra: {
      error,
      product,
    },
  })
}

export const captureLeadApiError = (product: Product, leadUuid: string, error: ErrorType) => {
  Sentry.captureMessage('Lead API Error', {
    extra: {
      error,
      leadUuid,
      product,
    },
  })
}

export const captureNoApiTokenWarning = () => {
  Sentry.captureMessage('No access token in URL Params', {
    level: Sentry.Severity.Warning,
  })
}

export const captureNoCompanyUuidWarning = () => {
  Sentry.captureMessage('No company uuid in URL Params', {
    level: Sentry.Severity.Warning,
  })
}

export const capturePostMessageWarning = (error: ErrorType) => {
  Sentry.captureMessage('Message to Helper Failed', {
    extra: {
      error,
    },
    level: Sentry.Severity.Warning,
  })
}

export const captureQueryParamParseError = (value: string, key: string) =>
  Sentry.captureMessage('Incorrectly formatted query param', {
    extra: {
      key,
      value,
    },
    level: Sentry.Severity.Warning,
  })

export const captureZeroOffersWarning = (rateTableUuid: string, product: Product) =>
  Sentry.captureMessage('Returned 0 offers', {
    level: Sentry.Severity.Warning,
    tags: {
      product,
      rateTableUuid,
    },
  })

export const captureHelperTimeout = () => {
  Sentry.captureMessage('Helper timed out finding iframe', {
    level: Sentry.Severity.Warning,
  })
}

export const captureCantSendHelperMessage = (iframeSrc: string) => {
  Sentry.captureMessage('Could not send Helper message', {
    extra: {
      href: window.location.href,
      iframeSrc,
    },
  })
}

export const captureNoChildElementWarning = () => {
  Sentry.captureMessage('Could not find child element', {
    extra: {
      href: window.location.href,
    },
  })
}

export const captureInvalidPartnerConfigResponseWarning = (response: unknown) => {
  Sentry.captureMessage('Invalid partner config response', {
    extra: {
      response,
    },
    level: Sentry.Severity.Warning,
  })
}

export const captureErrorOpeningWindowFromHelperEvent = (url: string) => {
  Sentry.captureMessage('Error opening window from helper script', {
    extra: {
      url,
    },
    level: Sentry.Severity.Warning,
  })
}

export const captureErrorOpeningWindowFromEmbedEvent = (url: string) => {
  Sentry.captureMessage('Error opening window from embed', {
    extra: {
      url,
    },
    level: Sentry.Severity.Warning,
  })
}

export const captureUniversityRequestError = (error: ErrorType) => {
  Sentry.captureMessage('University List Request Failed', {
    extra: {
      error,
    },
    level: Sentry.Severity.Warning,
  })
}

export const captureMultipleProductSubTypeRateTable = (uuid: string) => {
  Sentry.captureMessage('RateTable with Multiple ProductSubTypes', {
    extra: {
      rateTableUuid: uuid,
    },
    level: Sentry.Severity.Warning,
  })
}

export const captureInvalidProductStepUpdate = (step) => {
  Sentry.captureMessage('Invalid Product UI Step Update', {
    extra: {
      step,
    },
    level: Sentry.Severity.Warning,
  })
}

// Helper script is supposed to do redirects
// So partner sites arent logged in a little iframe
export const captureWarningForBackupRedirect = () => {
  Sentry.captureMessage('Embed had to Redirect Itself', {
    level: Sentry.Severity.Warning,
  })
}

export const captureSpendingCategoriesRequestError = (error: ErrorType) => {
  Sentry.captureMessage('Offer Augmentation Spending Categories Request Failed', {
    extra: {
      error,
    },
    level: Sentry.Severity.Warning,
  })
}

export const captureOfferRedirectUrlParseUuidError = (url: string) => {
  Sentry.captureMessage('Offer URL did not contain a UUID', {
    extra: {
      url,
    },
    level: Sentry.Severity.Warning,
  })
}

export const captureSessionInitFailedWarning = (error: ErrorType) => {
  Sentry.captureMessage('Session init failed', {
    extra: {
      error,
    },
    level: Sentry.Severity.Warning,
  })
}

export const captureLocalStorageAccessWarning = (error: ErrorType) => {
  Sentry.captureMessage('localStorage access denied', {
    extra: {
      error,
    },
    level: Sentry.Severity.Warning,
  })
}
